import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import MyNavbar from "./components/my-navbar/my-navbar.component";
import MyCarousal from "./components/my-carousal/my-carousal.component";
import FooterPanel from "./components/footer/footer.component";
import SpotlightGrid from "./components/spotlight-grid/SpotlightGrid";
import ScrollProgress from "./components/scroll-progress/ScrollProgress";
import ValentineGame from "./pages/valentine/ValentineGame";
import MothersDay from "./pages/mothersday/MothersDay";
import Container from "react-bootstrap/Container";
import Fade from "react-reveal/Fade";
import Slide from "react-reveal/Slide";
// Phase A3: dropped react-parallax usage. Dep stays in package.json
// until Phase C cutover for safe revert.
import posthog from 'posthog-js';
import './App.css';
// ML / research project images (Text-to-Image, YOLOv3, Emotion Recognition,
// Trading Bot) are no longer rendered on the home page as of Phase A6.
// Kept out of imports to avoid bundle bloat; restore them when wiring the
// /archive page.
import PDFCourtImage from "./assets/img/projects/pdfcourt-og.png";
import InfoQImage from "./assets/img/projects/infoq-dpop.jpg";
import AuthgentImage from "./assets/img/projects/authgent-card.png";
import HeadlessKitImage from "./assets/img/projects/headlesskit-card.png";
import ShuffleTurnImage from "./assets/img/projects/shuffleturn-card.png";

const About = lazy(() => import("./pages/about/about.component"));
const Skills = lazy(() => import("./pages/skills/skills.component"));
const ContactForm = lazy(() => import("./pages/contact-form/contact-form.component"));
const ProfileItem = lazy(() => import("./components/project-item/project-item.component"));
const PublicationsPage = lazy(() => import("./pages/publications/publications.component"));
const OpenSourcePage = lazy(() => import("./pages/open-source/open-source.component"));
const CaseStudiesIndex = lazy(() => import("./pages/case-studies/case-studies.component"));
const DPoPCaseStudy = lazy(() => import("./pages/case-studies/dpop-at-salesforce.component"));
const HeadlessKitCaseStudy = lazy(() => import("./pages/case-studies/why-headlesskit.component"));
const BlogIndex = lazy(() => import("./pages/blog/blog.component"));
const BlogPost = lazy(() => import("./pages/blog/blog-post.component"));
const NowPage = lazy(() => import("./pages/now/now.component"));
// Phase A5: hidden EB-1A evidence map. Linked from About bio + case-study footer.
const Credentials = lazy(() => import("./pages/credentials/credentials.component"));
// Phase A5: Person + WebSite JSON-LD mounted globally inside HelmetProvider.
const JsonLd = lazy(() => import("./components/json-ld/JsonLd"));
// const ExperienceConsolidated = lazy(() => import("./components/experience-timeline/experience-consolidated/experience-consolidated.component"));

// Phase A1: switched from <Timeline /> to <ExperienceList /> (editorial
// two-column date / role layout). Original Timeline component is preserved
// in src/components/timeline/ for one-line revert.
const ExperienceList = lazy(() => import("./components/experience-list/ExperienceList"));
// Phase A2: recruiter-in-5-seconds metric strip mounted above experience.
const CareerHighlights = lazy(() => import("./components/career-highlights/CareerHighlights"));
// Phase B2: "How I think" — 4 short opinions (2 tech, 2 team) that
// articulate judgment & taste, not just impact. Differentiator that
// distinguishes Staff portfolios from Senior ones.
const HowIThink = lazy(() => import("./components/how-i-think/HowIThink"));
const TechMarquee = lazy(() => import("./components/tech-marquee/TechMarquee"));
// const ScheduleMeeting = lazy(() => import("./components/ScheduleMeeting/ScheduleMeeting.component"));
// ML / research project detail components kept in the tree but no longer
// lazy-imported here (not rendered on the home page). Re-add imports when
// the /archive page is wired up. See Phase A6 comment above.
const PDFCourtDetails = lazy(() => import("./components/project-item/project-specific-content/pdf-court/pdf-court.component"));
const AuthgentDetails = lazy(() => import("./components/project-item/project-specific-content/authgent/authgent.component"));
const HeadlessKitDetails = lazy(() => import("./components/project-item/project-specific-content/headlesskit/headlesskit.component"));
const ShuffleTurnDetails = lazy(() => import("./components/project-item/project-specific-content/shuffleturn/shuffleturn.component"));
const InfoQDPoPDetails = lazy(() => import("./components/project-item/project-specific-content/infoq-dpop/infoq-dpop.component"));


posthog.init(process.env.REACT_APP_POSTHOG_KEY || 'phc_R16hMOMYRnSo2cTtsPG2HnAVSO7Y9T4tgtIEWA6VMLZ', {
  api_host: 'https://app.posthog.com',
  autocapture: true,
  capture_pageview: true,
  persistence: 'localStorage', // Optional: use local storage for persistence
  session_recording: {
    enabled: true,
    maskAllInputs: false
  }
});

// Custom hook to track page views
const usePageTracking = () => {
  useEffect(() => {
    const handleRouteChange = () => {
      posthog.capture('$pageview');
    };
    window.addEventListener('popstate', handleRouteChange);
    handleRouteChange();

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);
};

const App = () => {
  usePageTracking();

  // Track user interactions
  const handleButtonClick = (buttonName) => {
    posthog.capture('button_click', { button_name: buttonName });
  };

  const handleFormSubmit = (formName) => {
    posthog.capture('form_submit', { form_name: formName });
  };

  // Track section views
  useEffect(() => {
    const trackSectionVisibility = () => {
      const sections = ['home', 'about', 'Personalprojects', 'experienceTimeline', 'contact'];
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.3, // 30% visibility required to trigger
      };

      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            posthog.capture('section_view', { section: sectionId });
          }
        });
      }, observerOptions);

      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) sectionObserver.observe(element);
      });

      return () => sectionObserver.disconnect();
    };

    // Wait for DOM to fully load before tracking sections
    if (document.readyState === 'complete') {
      trackSectionVisibility();
    } else {
      window.addEventListener('load', trackSectionVisibility);
      return () => window.removeEventListener('load', trackSectionVisibility);
    }
  }, []);

  const MainPortfolio = () => (
    <div className="App" style={{ position: "relative" }}>
      <ScrollProgress />
      <MyCarousal />
      <MyNavbar />
      <Suspense fallback={<div>Loading...</div>}>
        {/* Phase A3: dropped <Parallax> wrapper. Parallax-on-scroll is a
            2017 trend that hurts perf and accessibility; About now sits in
            a flat container. To revert: re-wrap Container in Parallax props
            preserved in git history. */}
        <div>
          <Container className="container-box rounded">
            <Fade duration={500}>
              <About />
            </Fade>
          </Container>
        </div>
        {/* Staff-engineer flow: impact + experience FIRST (what I own at my
            day job), then independent work (InfoQ / Authgent / HeadlessKit),
            then side products, then skills. Personal projects are supporting
            evidence, not the headline. */}
        <Container fluid className="container-box rounded" id="careerHighlightsWrap">
          <hr />
          <CareerHighlights />
        </Container>
        <Container fluid className="container-box rounded ProjectOverflow experience-container-wrap" id='experienceTimeline'>
          <div>
            <hr />
            <h1 className="section-heading">Experience</h1>
            <p className="text-center" style={{ color: 'var(--color-fg-muted)', maxWidth: 720, margin: '0 auto 1.8rem auto', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Seven years at Salesforce (MTS &rarr; SMTS &rarr; LMTS), with an ML research background that&rsquo;s become the unfair advantage for the AI-agent + identity collision. Click any role for the full story.
            </p>
            <ExperienceList />
          </div>
        </Container>
        <Container fluid className="container-box rounded" id="howIThinkWrap">
          <hr />
          <HowIThink />
        </Container>
        <hr />
        <Container className="container-box rounded container ProjectOverflow" id='Personalprojects'>
          <h1 className="section-heading">Independent Work</h1>
          <p className="text-center" style={{ color: 'var(--color-fg-muted)', maxWidth: 720, margin: '0 auto 1.8rem auto', fontSize: '0.95rem', lineHeight: 1.6 }}>
            One peer-reviewed InfoQ paper. Two open-source identity servers &mdash; including <strong style={{ color: '#67e8f9' }}>Authgent</strong>, the first OAuth&nbsp;2.1 server purpose-built for <strong style={{ color: '#cbd5e1' }}>AI-agent delegation</strong>. The agentic web is the next 10 years of identity.
          </p>
          <SpotlightGrid className='rowForProjectsEditorial'>
            <ProfileItem
              displayMode="editorial"
              tags={['RFC 9449', 'OAuth 2.1', 'Browser security', 'Sole-authored']}
              backgroundImage={InfoQImage}
              project='InfoQ — The DPoP Storage Paradox'
              projectDescription='Sole-authored InfoQ article on browser-OAuth security and RFC 9449. Published April 30, 2026.'
              projectComponent={<InfoQDPoPDetails />}
              demoUrl='https://www.infoq.com/articles/dpop-key-storage-unsolved-problem/'
              onClick={() => handleButtonClick('InfoQ DPoP Article')}
            />
            <ProfileItem
              displayMode="editorial"
              tags={['OAuth 2.1', 'AI agents', 'Python', 'Open source']}
              backgroundImage={AuthgentImage}
              imageFit="contain"
              project='Authgent'
              projectDescription='OAuth 2.1 server purpose-built for AI agents. Delegation, scope narrowing, kill switch.'
              projectComponent={<AuthgentDetails />}
              demoUrl='https://github.com/authgent/authgent'
              repoUrl='https://github.com/authgent/authgent'
              onClick={() => handleButtonClick('Authgent Project')}
            />
            <ProfileItem
              displayMode="editorial"
              tags={['Auth + Payments', 'Flask + React', 'PyPI · npm', '~540 dl/mo']}
              backgroundImage={HeadlessKitImage}
              imageFit="contain"
              project='HeadlessKit'
              projectDescription='Self-hosted auth + payments suite. Flask + React, 4 published packages.'
              projectComponent={<HeadlessKitDetails />}
              demoUrl='https://github.com/Dhruvagnihotri/headlesskits'
              repoUrl='https://github.com/Dhruvagnihotri/headlesskits'
              onClick={() => handleButtonClick('HeadlessKit Project')}
            />
          </SpotlightGrid>

          {/* Tier-2 side products. Kept for "I ship" proof, but clearly
              separated from the auth/identity prestige tier above so they
              don't dilute the niche. */}
          <h2 className="section-heading" style={{ marginTop: '3.5rem', fontSize: '1.4rem' }}>Also shipping</h2>
          <p className="text-center" style={{ color: 'var(--color-fg-muted)', maxWidth: 720, margin: '0 auto 1.3rem auto', fontSize: '0.9rem', lineHeight: 1.55 }}>
            Small live products I maintain on the side. Proof of shipping cadence; not the main line of work.
          </p>
          <SpotlightGrid className='rowForProjectsEditorial'>
            <ProfileItem
              displayMode="editorial"
              tags={['Privacy-first', 'PDF tools', 'Live · ~150/mo']}
              backgroundImage={PDFCourtImage}
              imageFit="contain"
              project='PDFCourt.com'
              projectDescription='A privacy-first PDF toolkit (compress, convert, organize, e-sign).'
              projectComponent={<PDFCourtDetails />}
              demoUrl='https://pdfcourt.com'
              onClick={() => handleButtonClick('PDFCourt.com Project')}
            />
            <ProfileItem
              displayMode="editorial"
              tags={['Real-time', 'Self-hosted', 'Live · ~150/mo']}
              backgroundImage={ShuffleTurnImage}
              imageFit="contain"
              project='ShuffleTurn'
              projectDescription='Real-time fair turn rotation for groups. Live, self-hosted.'
              projectComponent={<ShuffleTurnDetails />}
              demoUrl='https://shuffleturn.com'
              onClick={() => handleButtonClick('ShuffleTurn Project')}
            />
          </SpotlightGrid>

          {/* ML / research grid moved off home page as of Phase A6:
              2017–2019 era demos (YOLOv3, Text-to-Image, Trading Bot) were
              signalling junior / grad-student on a Staff-IC page. Emotion
              Recognition (DENSO) lives in the Experience timeline where it
              belongs. Components stay imported for /archive if restored.
              Revert: re-insert the original <SpotlightGrid rowForProjects>
              block preserved in git history. */}
        </Container>
        <Container fluid className="container-box rounded" id="techMarqueeWrap">
          <hr />
          <TechMarquee />
        </Container>
        <Container fluid className="container-box rounded skills-container-wrap">
          <Slide bottom duration={500}>
            <hr />
            <Skills />
          </Slide>
        </Container>
        <Container className="container-box rounded" id="contact">
          <hr />
          <ContactForm onSubmit={() => handleFormSubmit('Contact Form')} />
        </Container>
      </Suspense>
      <FooterPanel />
    </div>
  );

  const SubPageLayout = ({ children }) => (
    <div className="App" style={{ position: "relative" }}>
      <ScrollProgress />
      <MyNavbar />
      <Suspense fallback={<div style={{ color: "#fff", padding: "8rem 1rem", textAlign: "center" }}>Loading…</div>}>
        {children}
      </Suspense>
      <FooterPanel />
    </div>
  );

  return (
    <HelmetProvider>
      <Helmet>
        <title>Dhruv Agnihotri — LMTS @ Salesforce · Auth for AI Agents · InfoQ Author</title>
        <meta
          name="description"
          content="Dhruv Agnihotri — Lead Member of Technical Staff at Salesforce. Building the auth stack for AI agents (Authgent — first OAuth 2.1 server for agentic delegation) and the integration layer Salesforce's Marketing platform runs on. InfoQ-published, sole-authored on DPoP."
        />
        <link rel="canonical" href="https://dhruvagnihotri.com/" />
      </Helmet>
      <Suspense fallback={null}>
        <JsonLd />
      </Suspense>
      <BrowserRouter>
        <Routes>
          <Route path="/valentine" element={<ValentineGame />} />
          <Route path="/happymothersday" element={<MothersDay />} />
          <Route
            path="/publications"
            element={
              <SubPageLayout>
                <PublicationsPage />
              </SubPageLayout>
            }
          />
          <Route
            path="/open-source"
            element={
              <SubPageLayout>
                <OpenSourcePage />
              </SubPageLayout>
            }
          />
          <Route
            path="/case-studies"
            element={
              <SubPageLayout>
                <CaseStudiesIndex />
              </SubPageLayout>
            }
          />
          <Route
            path="/case-studies/dpop-at-salesforce"
            element={
              <SubPageLayout>
                <DPoPCaseStudy />
              </SubPageLayout>
            }
          />
          <Route
            path="/case-studies/why-headlesskit"
            element={
              <SubPageLayout>
                <HeadlessKitCaseStudy />
              </SubPageLayout>
            }
          />
          <Route
            path="/blog"
            element={
              <SubPageLayout>
                <BlogIndex />
              </SubPageLayout>
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <SubPageLayout>
                <BlogPost />
              </SubPageLayout>
            }
          />
          <Route
            path="/now"
            element={
              <SubPageLayout>
                <NowPage />
              </SubPageLayout>
            }
          />
          <Route
            path="/credentials"
            element={
              <SubPageLayout>
                <Credentials />
              </SubPageLayout>
            }
          />
          <Route path="*" element={<MainPortfolio />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
